'use client';

import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common/Forms/Button/Button';
import { logRouteError } from '@utils/logger/logger';
import { isStaleBuildError, reloadOnceForStaleBuild } from '@utils/stale-build/staleBuild';

interface RouteErrorProps {
  error: Error & { digest?: string };
}

/**
 * Route-level error boundary for everything under `[locale]`. Without it, any uncaught client
 * render error fell through to Next's built-in "This page couldn’t load" screen and was never
 * logged. Rendered inside the locale layout, so header, translations and styles stay available.
 *
 * Errors caused by a deploy (stale chunks / Server Action IDs, see `isStaleBuildError`) trigger one
 * automatic reload. "Try again" is a full reload too, not `reset()`: re-rendering can't recover from
 * a failed chunk load, because Turbopack caches the rejection for the page's lifetime.
 */
export default function RouteError({ error }: RouteErrorProps) {
  const { t } = useTranslation('common');

  useEffect(() => {
    logRouteError('RouteError', error);
    if (isStaleBuildError(error)) {
      reloadOnceForStaleBuild();
    }
  }, [error]);

  return (
    <div className="flex grow items-center justify-center bg-surface-100 px-4 py-16">
      <div className="w-full max-w-md rounded-md bg-surface p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto size-10 text-ink-medium" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-bold text-ink">{t('error.boundary.label.title')}</h1>
        <p className="mt-2 text-sm text-ink-medium">{t('error.boundary.label.description')}</p>
        <div className="mt-6 flex justify-center gap-2">
          <Button rounding="full" size="sm" color="secondary" onClick={() => window.location.reload()}>
            {t('error.boundary.label.button.try-again')}
          </Button>
          <Button rounding="full" size="sm" styling="outline" color="ink-medium" onClick={() => window.history.back()}>
            {t('global.action.back')}
          </Button>
        </div>
      </div>
    </div>
  );
}
